import React, { useMemo } from 'react'
import { GlobalOutlined, PushpinFilled, PushpinOutlined } from '@ant-design/icons'
import { Avatar, Table, Tooltip, theme } from 'antd'
import { BadgeTag } from '@pushly/aqe/lib/components'
import type { ColumnsType } from 'antd/es/table'
import { DashboardDomain, GoodDirection } from '../../types'
import {
    DASHBOARD_TABLE_ROW_CLASS_NAME,
    DASHBOARD_TABLE_SIZE,
    dashboardTableClassName,
} from '../../components/dashboard-table-parts'
import { DefinitionTooltip } from '../../components/definition-tooltip'
import { DeltaText } from '../../components/delta-text'
import { NoDomains, NoDomainsMatchSearch } from '../../components/domain-empty-states'
import { OutlierTag } from '../../components/outlier-tag'
import { Sparkline } from '../../components/sparkline'
import { TABLE_TOTALS_ROW_CLASS_NAME, TotalsFigure, TotalsLabel } from '../../components/table-totals'
import { DASHBOARD_TABLE_PAGE_SIZE } from '../../components/table-page-size'
import { DOMAINS_TABLE_ROW_HEIGHT, tableRowHeightStyle } from '../../components/table-row-heights'
import { NO_CURRENCY_NAME, NO_VALUE, currencyListFor, formatShortDateRange } from '../../math/format'
import { DateWindow } from '../../math/windows'
import { DashboardTemplate, TEMPLATE_SLOTS } from '../../template'
import {
    DomainCell,
    DomainRow,
    DomainTotalsCell,
    DomainTotalsRow,
    CONTEXT_RATE_NAME_BY_TEMPLATE,
    SINGLE_CURRENCY_CELL_KEYS,
    contextGoodDirectionMeasureFor,
    goodDirectionMeasureFor,
} from './domains-model'

export type DomainsSort = { column: string; direction: 'asc' | 'desc' }

const COLUMN_LABELS: Record<string, string> = {
    subscribers: 'Subscribers',
    new_subscribers: 'New Subs',
    sends: 'Sends',
    clicks: 'Clicks',
    ctr: 'CTR',
    revenue: 'Revenue',
    purchases: 'Purchases',
    revenue_per_send: 'Rev / Send',
}

const COLUMN_DEFINITIONS: Record<string, string> = {
    subscribers: 'Subscribers active on the last day of the range',
    ctr: 'CTR = clicks ÷ impressions',
    revenue_per_send: 'revenue ÷ notifications sent',
}

/** What the Context figure compares, in the template's own rate: a domain is only ever compared with itself. */
const contextDefinition = (template: DashboardTemplate): string =>
    `The range's ${CONTEXT_RATE_NAME_BY_TEMPLATE[template]} against this domain's own average over the 28 days before the range. Gray means within its normal variation. A domain is never compared with another.`

const DOMAIN_COLUMN_WIDTH = 240
/**
 * Holds a sparkline, a gap and the longest whole count a domain plausibly reaches ("12,345,678"), plus the cell's
 * padding. Counts are written in full, so this is sized by the figure; the longest heading ("Subscribers") with its
 * sort arrow and info icon fits well inside it.
 */
export const NUMERIC_COLUMN_WIDTH = 152
/** The same for an amount of money with its currency symbol and cents ("$1,234,567.89"), which is wider than a count. */
export const MONEY_COLUMN_WIDTH = 176
const CONTEXT_COLUMN_WIDTH = 150

/** The table scrolls sideways below the width its columns need, which its fixed minimums reserve. */
export const DOMAINS_TABLE_SCROLL = { x: 'max-content' } as const
export const DOMAINS_TABLE_CLASS_NAME = 'org-dashboard-domains-table'

type ColumnFrameOptions = {
    sort: DomainsSort
    /** Revenue figures of several currencies cannot be ordered, so those headings carry no sorter. */
    mixedCurrency: boolean
}

/**
 * Everything about the Domains table's columns that does not depend on a row: headings, widths, alignment, the
 * pinned first column and the sorter. The loaded table adds its cell renderers to these, and the loading
 * placeholder renders the same frames, so the two have the same headings and the same widths.
 */
export const domainsColumnFrames = (
    template: DashboardTemplate,
    { sort, mixedCurrency }: ColumnFrameOptions,
): ColumnsType<DomainRow> => {
    const domainFrame: ColumnsType<DomainRow>[number] = {
        key: 'domain',
        title: 'Domain',
        fixed: 'left',
        width: DOMAIN_COLUMN_WIDTH,
        className: 'org-dashboard-domains-column-domain',
    }
    const numericFrames: ColumnsType<DomainRow> = TEMPLATE_SLOTS[template].domainsColumns
        .filter((key) => key !== 'context')
        .map((key) => {
            const sortable = !(mixedCurrency && SINGLE_CURRENCY_CELL_KEYS.includes(key))
            const definition = COLUMN_DEFINITIONS[key]
            const money = SINGLE_CURRENCY_CELL_KEYS.includes(key)
            return {
                key,
                title: (
                    <span>
                        {COLUMN_LABELS[key]}
                        {definition ? <DefinitionTooltip text={definition} /> : null}
                    </span>
                ),
                align: 'right',
                width: money ? MONEY_COLUMN_WIDTH : NUMERIC_COLUMN_WIDTH,
                className: money ? 'org-dashboard-domains-column-money' : 'org-dashboard-domains-column-numeric',
                sorter: sortable,
                sortDirections: ['descend', 'ascend'],
                showSorterTooltip: false,
                sortOrder: sortable && sort.column === key ? (sort.direction === 'asc' ? 'ascend' : 'descend') : null,
            }
        })
    const contextFrame: ColumnsType<DomainRow>[number] = {
        key: 'context',
        title: (
            <span>
                Context
                <DefinitionTooltip text={contextDefinition(template)} />
            </span>
        ),
        width: CONTEXT_COLUMN_WIDTH,
        className: 'org-dashboard-domains-column-context',
    }
    return [domainFrame, ...numericFrames, contextFrame]
}

type Props = {
    template: DashboardTemplate
    /** In display order, after any search. */
    rows: DomainRow[]
    /** The totals row's figures, keyed like the cells. */
    totals: DomainTotalsRow
    /** Every domain in the current filter, regardless of search. */
    domainCount: number
    sentDomainCount: number
    /** The distinct currencies of the domains in the filter. More than one means revenue cannot be totalled or sorted. */
    currencyCodes: Array<string | undefined>
    comparisonWindow: DateWindow
    goodDirectionFor: (measure: string) => GoodDirection | undefined
    sort: DomainsSort
    onSortChange: (sort: DomainsSort | null) => void
    currentPage: number
    onPageChange: (page: number) => void
    searchTerm: string
    onClearSearch: () => void
    onTogglePin: (domainId: number) => void
    onOpenDomain: (domain: DashboardDomain, event: React.MouseEvent<HTMLAnchorElement>) => void
    /** Where the domain's own dashboard lives, so a middle-click or copied link still works. */
    domainHref: (domain: DashboardDomain) => string
    /** A domain the user cannot switch to is shown as plain text. */
    canOpenDomain: (domain: DashboardDomain) => boolean
}

/** A pinned pushpin is filled and in the warning colour, an unpinned one an outline, so the state never rests on colour alone. */
const PinButton: React.FC<{ row: DomainRow; onTogglePin: (domainId: number) => void }> = ({ row, onTogglePin }) => {
    const { token } = theme.useToken()
    return (
        <Tooltip title={row.pinned ? 'Unpin' : 'Pin to top'}>
            <button
                type="button"
                className="org-dashboard-pin-button"
                aria-pressed={row.pinned}
                aria-label={`${row.pinned ? 'Unpin' : 'Pin'} ${row.domain.displayName}`}
                onClick={() => onTogglePin(row.domain.id)}
            >
                {row.pinned ? (
                    <PushpinFilled aria-hidden="true" style={{ color: token.colorWarning }} />
                ) : (
                    <PushpinOutlined aria-hidden="true" />
                )}
            </button>
        </Tooltip>
    )
}

const NumericCell: React.FC<{
    cell: DomainCell
    goodDirection: GoodDirection | undefined
    comparisonLabel: string
}> = ({ cell, goodDirection, comparisonLabel }) => (
    <div className="org-dashboard-domains-cell">
        <div className="org-dashboard-domains-cell-main">
            {cell.sparkline.length > 0 ? <Sparkline values={cell.sparkline} variant="table" /> : null}
            <span className={cell.value === null ? 'org-dashboard-dash' : undefined}>{cell.formatted}</span>
        </div>
        <DeltaText
            className="org-dashboard-domains-delta"
            value={cell.delta.value}
            kind={cell.delta.kind}
            goodDirection={goodDirection}
            steady={cell.delta.steady}
            comparisonLabel={comparisonLabel}
        />
    </div>
)

const ContextCell: React.FC<{
    row: DomainRow
    goodDirection: GoodDirection | undefined
    comparisonLabel: string
}> = ({ row, goodDirection, comparisonLabel }) => {
    const { token } = theme.useToken()
    return (
        <div className="org-dashboard-domains-context">
            {row.sentInRange ? (
                <>
                    <DeltaText
                        className={row.context.significant ? undefined : 'org-dashboard-context-steady'}
                        value={row.context.pct}
                        kind="count"
                        goodDirection={row.context.significant ? goodDirection : 'none'}
                        steady={false}
                        comparisonLabel={comparisonLabel}
                    />
                </>
            ) : (
                <>
                    <span className="org-dashboard-dash">{NO_VALUE}</span>
                    <BadgeTag
                        variant="filled"
                        color={token.colorTextSecondary}
                        className="org-dashboard-tag org-dashboard-context-tag"
                        label={row.context.label}
                    />
                </>
            )}
            {row.outlier ? <OutlierTag days={row.outlier.days} operational={row.outlier.operational} /> : null}
        </div>
    )
}

/** Said while a revenue total is withheld; focusable so keyboard users can read why, which the tooltip describes. */
const MixedCurrenciesTotal: React.FC<{ text: string; currencyCodes: Array<string | undefined> }> = ({
    text,
    currencyCodes,
}) => (
    <Tooltip
        title={`Selected domains report in ${currencyListFor(currencyCodes)}. Filter to one currency to see a total.`}
        trigger={['hover', 'focus']}
    >
        <span className="org-dashboard-mixed-currencies" tabIndex={0} role="button">
            {text}
        </span>
    </Tooltip>
)

/** A total's figure on its own, so the Total row is one line. A withheld revenue total keeps its explanation reachable. */
const TotalsCellFigure: React.FC<{
    cellKey: string
    cell: DomainTotalsCell
    mixedCurrency: boolean
    currencyCodes: Array<string | undefined>
}> = ({ cellKey, cell, mixedCurrency, currencyCodes }) =>
    mixedCurrency && SINGLE_CURRENCY_CELL_KEYS.includes(cellKey) ? (
        <MixedCurrenciesTotal text={cell.text} currencyCodes={currencyCodes} />
    ) : (
        <TotalsFigure text={cell.text} />
    )

export const DomainsTable: React.FC<Props> = ({
    template,
    rows,
    totals,
    domainCount,
    sentDomainCount,
    currencyCodes,
    comparisonWindow,
    goodDirectionFor,
    sort,
    onSortChange,
    currentPage,
    onPageChange,
    searchTerm,
    onClearSearch,
    onTogglePin,
    onOpenDomain,
    domainHref,
    canOpenDomain,
}) => {
    const numericKeys = TEMPLATE_SLOTS[template].domainsColumns.filter((key) => key !== 'context')
    const mixedCurrency = currencyCodes.length > 1
    const comparisonLabel = `vs ${formatShortDateRange(comparisonWindow)}`

    const columns: ColumnsType<DomainRow> = useMemo(() => {
        const renderDomain: ColumnsType<DomainRow>[number]['render'] = (_, row) => (
            <div className="org-dashboard-domain-cell">
                <PinButton row={row} onTogglePin={onTogglePin} />
                <Avatar size="small" icon={<GlobalOutlined aria-hidden="true" />} />
                <div className="org-dashboard-domain-cell-text">
                    {canOpenDomain(row.domain) ? (
                        <a href={domainHref(row.domain)} onClick={(event) => onOpenDomain(row.domain, event)}>
                            {row.domain.displayName}
                        </a>
                    ) : (
                        <span>{row.domain.displayName}</span>
                    )}
                    <div className="org-dashboard-domain-cell-hostname">
                        {mixedCurrency
                            ? `${row.domain.hostname} · ${row.domain.currencyCode ?? NO_CURRENCY_NAME}`
                            : row.domain.hostname}
                    </div>
                </div>
            </div>
        )
        const renderContext: ColumnsType<DomainRow>[number]['render'] = (_, row) => (
            <ContextCell
                row={row}
                goodDirection={goodDirectionFor(contextGoodDirectionMeasureFor(template))}
                comparisonLabel="vs own 28-day average"
            />
        )
        return domainsColumnFrames(template, { sort, mixedCurrency }).map((frame) => {
            const key = String(frame.key)
            if (key === 'domain') return { ...frame, render: renderDomain }
            if (key === 'context') return { ...frame, render: renderContext }
            return {
                ...frame,
                render: (_: unknown, row: DomainRow) => (
                    <NumericCell
                        cell={row.cells[key]}
                        goodDirection={goodDirectionFor(goodDirectionMeasureFor(key))}
                        comparisonLabel={comparisonLabel}
                    />
                ),
            }
        })
    }, [
        template,
        mixedCurrency,
        goodDirectionFor,
        sort,
        comparisonLabel,
        onTogglePin,
        onOpenDomain,
        domainHref,
        canOpenDomain,
    ])

    return (
        <Table<DomainRow>
            className={dashboardTableClassName(DOMAINS_TABLE_CLASS_NAME)}
            size={DASHBOARD_TABLE_SIZE}
            rowClassName={DASHBOARD_TABLE_ROW_CLASS_NAME}
            aria-label="Domains"
            rowKey={(row) => row.domain.id}
            columns={columns}
            dataSource={rows}
            scroll={DOMAINS_TABLE_SCROLL}
            style={tableRowHeightStyle(DOMAINS_TABLE_ROW_HEIGHT)}
            locale={{
                emptyText: searchTerm ? (
                    <NoDomainsMatchSearch searchTerm={searchTerm} onClearSearch={onClearSearch} />
                ) : (
                    <NoDomains />
                ),
            }}
            pagination={{
                current: currentPage,
                pageSize: DASHBOARD_TABLE_PAGE_SIZE,
                showSizeChanger: false,
                showTotal: (total, [from, to]) => `${from}–${to} of ${total} domains`,
                hideOnSinglePage: true,
                placement: ['bottomEnd'],
            }}
            onChange={(pagination, _filters, sorter, extra) => {
                if (extra.action === 'sort') {
                    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter
                    onSortChange(
                        activeSorter?.order && activeSorter.columnKey
                            ? {
                                  column: String(activeSorter.columnKey),
                                  direction: activeSorter.order === 'ascend' ? 'asc' : 'desc',
                              }
                            : null,
                    )
                } else if (extra.action === 'paginate') {
                    onPageChange(pagination.current ?? 1)
                }
            }}
            summary={() => (
                <Table.Summary fixed="bottom">
                    <Table.Summary.Row className={TABLE_TOTALS_ROW_CLASS_NAME}>
                        <Table.Summary.Cell index={0}>
                            <TotalsLabel
                                coverage={`${sentDomainCount} of ${domainCount} sent`}
                                coverageInFull={`${sentDomainCount} of ${domainCount} domains sent`}
                            />
                        </Table.Summary.Cell>
                        {numericKeys.map((key, index) => (
                            <Table.Summary.Cell index={index + 1} key={key} align="right">
                                <TotalsCellFigure
                                    cellKey={key}
                                    cell={totals[key]}
                                    mixedCurrency={mixedCurrency}
                                    currencyCodes={currencyCodes}
                                />
                            </Table.Summary.Cell>
                        ))}
                        <Table.Summary.Cell index={numericKeys.length + 1} />
                    </Table.Summary.Row>
                </Table.Summary>
            )}
        />
    )
}
